import { useQuery } from "@tanstack/react-query";
// Use the data provider's validated resolver (origin-allowlisted per #5097) — do
// not re-implement partner URL resolution, or this token-bearing call becomes an
// unvalidated exfiltration sink.
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import type { StoplossClaimsDashboardAnalytics } from "@/types";

interface ClaimsDashboardFilters {
  client_id_ref?: string | string[];
  vendor_id_ref?: string | string[]; // carrier
  date_from?: string; // YYYY-MM-DD
  date_to?: string; // YYYY-MM-DD
}

/**
 * Fetches all claims-dashboard data from the single analytics endpoint.
 * Replaces the old dashboard's three 10,000-row list fetches + client-side
 * aggregation with one pre-aggregated request.
 */
export const useStoplossClaimsDashboardData = (filters: ClaimsDashboardFilters) => {
  return useQuery<StoplossClaimsDashboardAnalytics>({
    queryKey: ["stoploss-claims-dashboard-analytics", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      const appendFilter = (key: string, value?: string | string[]) => {
        if (!value) return;
        const values = Array.isArray(value) ? value : [value];
        values.forEach((v) => v && params.append(key, v));
      };
      appendFilter("client_id_ref", filters.client_id_ref);
      appendFilter("vendor_id_ref", filters.vendor_id_ref);
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);

      const qs = params.toString();
      const url = `${getPartnerAwareApiUrl()}/stoploss-claims/dashboard/analytics${qs ? `?${qs}` : ""}`;
      const { json } = await httpClient(url);
      return json as StoplossClaimsDashboardAnalytics;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};
